import {
  Alert,
  Button,
  Group,
  Radio,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconPhoto,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { DeliveryAgentPicker } from "../../components/DeliveryAgentPicker";
import { ProductPicker, type OrderLine } from "../../components/ProductPicker";
import {
  ErrorState,
  LoadingSkeleton,
  MoneyField,
  RecordWithRail,
  SectionCard,
  StageStepper,
  StateBadge,
  StickyActionBar,
  type Stage,
} from "../../components/ui";
import { serviceJobState } from "../../components/ui/statusBadge";
import { api, type ServiceJobDetail as Job, type ServiceOutcome } from "../../lib/api";
import { hierarchyPath } from "../../lib/hierarchy";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";
import { OUTCOMES, STATUS_LABELS, WARRANTY_LABELS } from "./serviceMeta";

/**
 * Mirrors the per-endpoint role sets in src/routes/service.routes.ts, and
 * test/service.test.ts fails if the two drift.
 *
 * These are plain strings, so the compiler said nothing when the roles were
 * renamed - DIAGNOSES still read SERVICE_TECHNICIAN and quietly hid the
 * diagnosis form from every technician. Hence the guard.
 */
const RECEIVES = ["SERVICE_MANAGER", "STORE_MANAGER", "ADMIN"];
const DIAGNOSES = [
  "INDOOR_SERVICE_TECHNICIAN",
  "SERVICE_MANAGER",
  "STORE_MANAGER",
  "ADMIN",
];
const COMPLETES = ["SERVICE_MANAGER", "STORE_MANAGER", "ADMIN"];

/** A label/value row in one of the rail's panels. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
      <Text component="dt" fz="sm" c="gray.6" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text component="dd" fz="sm" c="gray.9" m={0} ta="right" style={{ minWidth: 0 }}>
        {children}
      </Text>
    </Group>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <Stack gap={8} component="dl" m={0}>
      {children}
    </Stack>
  );
}

const when = (iso: string) => new Date(iso).toLocaleString();

/**
 * The five stages, with who did each and when.
 *
 * BUILT FOR OUR LIFECYCLE. The design shows Received / Diagnosed / Under
 * Repair / Ready / Delivered; there is no repair bench behind this app and no
 * "Ready" state, so drawing those would be a stepper lying about where the
 * battery is.
 */
function stagesOf(job: Job): { stages: Stage[]; current: number } {
  const stages: Stage[] = [
    {
      key: "collected",
      label: "Collected",
      at: when(job.collectedAt),
      ...(job.collectionAgent ? { by: job.collectionAgent.name } : {}),
    },
    {
      key: "received",
      label: "At the workshop",
      ...(job.receivedAt ? { at: when(job.receivedAt) } : {}),
      ...(job.serviceManager ? { by: job.serviceManager.name } : {}),
    },
    {
      key: "diagnosed",
      label: "Diagnosed",
      ...(job.diagnosedAt ? { at: when(job.diagnosedAt) } : {}),
      ...(job.technician ? { by: job.technician.name } : {}),
    },
    {
      key: "outcome",
      label: job.outcome ? (job.outcomeLabel ?? job.outcome) : "Outcome",
    },
    { key: "delivered", label: "Delivered back" },
  ];

  const current =
    job.status === "COLLECTED"
      ? 0
      : job.status === "RECEIVED"
        ? 1
        : job.status === "DIAGNOSED"
          ? 2
          : job.status === "DELIVERED"
            ? 4
            : 3;

  return { stages, current };
}

/**
 * One job, and whichever step it is waiting for.
 *
 * Steps 2, 3 and 4 live together because they are the same physical object
 * moving across a bench - splitting them across screens would mean navigating
 * between them to answer "what does this one need". Only the step the job is
 * actually at is shown, and only to a role that owns it: a technician sees
 * the diagnosis form but not the order step, which is the separation the API
 * enforces independently.
 *
 * WHAT THE DESIGN SHOWS THAT IS NOT HERE
 *
 * The desktop layout in the PDF carries a parts-used table with "+ Add part"
 * and a charges block with Labour, Discount and Technician inputs. Neither is
 * built, and neither is an oversight:
 *
 *  - there is no parts model anywhere in this system, so a parts table would
 *    be a form with nothing behind it;
 *  - the business had the service charge REMOVED from this module outright.
 *    Putting a labour field back would contradict a stated requirement, and
 *    the brief's own rule is not to change amounts to suit a layout.
 *
 * The one charge that does exist - the pro-rata amount - is on the outcome
 * step below, where the person who decides it works.
 */
export function ServiceJobDetail() {
  const { id = "" } = useParams();
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const [job, setJob] = useState<Job>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  // diagnosis
  const [outcome, setOutcome] = useState("");
  const [diagnosisNotes, setDiagnosisNotes] = useState("");

  // outcome / order
  const [lines, setLines] = useState<OrderLine[]>([]);
  /** The pro-rata charge, as typed. Kept loose so the field can be empty and
   *  a half-typed "12." does not become 12. */
  const [prorataAmount, setProrataAmount] = useState<number | string>("");
  /**
   * Who takes the battery back, chosen by the Service Manager.
   *
   * Starts EMPTY, on all four outcomes. It could have been pre-filled with
   * the collection agent - they have been to the address and carried this
   * battery once - and that is what the app used to do on its own. The
   * business asked for the choice to be deliberate instead, so the collection
   * agent is named beside the field rather than filled into it.
   */
  const [deliveryAgentId, setDeliveryAgentId] = useState("");
  /**
   * Set once the outcome has been carried out. `reference` is absent for an
   * OK or a rejection, because those raise no order at all.
   */
  const [finished, setFinished] = useState<{
    // ServiceOutcome, not string. As `string` it accepted a comparison
    // against "SERVICEABLE" long after that outcome stopped existing - the
    // branch simply never ran, and an OK job showed the rejection message.
    // The compiler catches it now.
    outcome: ServiceOutcome | null;
    reference?: string;
    type?: string;
    orderId?: string;
  }>();

  async function load() {
    try {
      setError(undefined);
      const { job: loaded } = await api.serviceJob(id);
      setJob(loaded);
      if (loaded.outcome) setOutcome(loaded.outcome);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the job.");
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The union of their roles. A technician who also collects batteries holds
  // both, and asking about one role would have hidden the diagnosis form from
  // exactly the person who has to fill it in.
  const canReceive = hasRole(...RECEIVES);
  const canDiagnose = hasRole(...DIAGNOSES);
  const canComplete = hasRole(...COMPLETES);

  /**
   * The pro-rata charge, and what it will actually come to.
   *
   * Shown before the manager confirms, because the amount they enter is the
   * TAXABLE value and GST goes on top - every price in this system is pre-GST
   * and the bill adds it. A manager who thought they were agreeing the final
   * figure would be out by the GST, on a document the customer keeps, so the
   * screen states both rather than leaving it to be discovered on the invoice.
   */
  const isProrata = job?.outcome === "PRORATA";
  const prorataValue = Number(prorataAmount);
  const prorataValid =
    String(prorataAmount).trim() !== "" &&
    Number.isFinite(prorataValue) &&
    prorataValue > 0 &&
    Math.round(prorataValue * 100) === Number((prorataValue * 100).toFixed(0));
  const linesGross = lines.reduce(
    (sum, line) => sum + line.unitPrice * line.quantity,
    0,
  );
  const prorataOverValue = prorataValid && prorataValue > linesGross;
  // 18% on every battery in the master list, and read off the line rather
  // than assumed - a differently rated accessory would otherwise be shown a
  // figure that is quietly wrong.
  const gstRate = 18;
  const prorataWithGst = prorataValue * (1 + gstRate / 100);

  // No rule left beyond "say which of the four, and what you found". The
  // outcome used to have to agree with a warranty status and a repairable
  // flag; both are gone, so there is nothing to disagree with.
  const diagnosisBlocker = outcome ? undefined : "Choose an outcome.";
  const diagnosisReady =
    !diagnosisBlocker && diagnosisNotes.trim().length >= 3 && !busy;

  async function act(what: () => Promise<void>, failure: string) {
    setBusy(true);
    setError(undefined);
    try {
      await what();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : failure;
      setError(message);
      notifications.show({ color: "red", autoClose: false, message });
    } finally {
      setBusy(false);
    }
  }

  /** The two outcomes that ISSUE a battery are the two that raise an order. */
  const raisesOrder = job?.outcome === "FOC" || job?.outcome === "PRORATA";

  if (finished) {
    // Two quite different endings, and the screen has to say which. A
    // replacement produces an order somebody still has to bill; an OK or a
    // rejection is simply done, and the battery goes back.
    const raisedOrder = finished.reference !== undefined;
    return (
      <Page
        title={raisedOrder ? "Order placed" : "Ready to go back"}
        subtitle={finished.reference ?? job?.reference}
      >
        {raisedOrder ? (
          <>
            <Alert
              variant="light"
              color="teal"
              radius="md"
              icon={<IconCircleCheck size={20} stroke={1.8} />}
              // It used to say "warranty replacement" here for every order.
              // Wrong twice over: warranty is not judged in this app any
              // more, and a pro-rata replacement is not free.
              title={`${finished.reference} — ${
                finished.type === "PRORATA"
                  ? "pro-rata replacement"
                  : "free replacement"
              }`}
            >
              It is now in the Billing Executive&apos;s queue. Stock is deducted
              when they generate the bill, not now.
            </Alert>
            <Button size="md" onClick={() => navigate(`/sales/bill/${finished.orderId}`)}>
              Go to billing
            </Button>
          </>
        ) : (
          <>
            <Alert
              variant="light"
              color="teal"
              radius="md"
              icon={<IconCircleCheck size={20} stroke={1.8} />}
              title={
                finished.outcome === "OK"
                  ? "OK. The customer's own battery goes back to them."
                  : "Rejected. The battery goes back to its owner."
              }
            >
              No order was raised and there is nothing to bill — nothing was
              sold. It is waiting for a delivery agent to take it back.
            </Alert>
            <Button size="md" onClick={() => navigate("/service/delivery")}>
              See what is ready for delivery
            </Button>
          </>
        )}
        <Button variant="default" size="md" onClick={() => navigate("/service/queue")}>
          Back to the queue
        </Button>
      </Page>
    );
  }

  if (error && !job) {
    return (
      <Page title="Service job" back="/service/queue">
        <ErrorState message={error} onRetry={() => void load()} />
      </Page>
    );
  }

  if (!job) {
    return (
      <Page title="Service job" back="/service/queue">
        <LoadingSkeleton variant="detail" />
      </Page>
    );
  }

  const { stages, current } = stagesOf(job);

  const battery = (
    <SectionCard title="The battery">
      <Panel>
        <Row label="Serial number">
          {job.serialNumber ? (
            <span className="num">{job.serialNumber}</span>
          ) : (
            <Text component="span" fz="sm" c="gray.6">
              Not recorded — collected before serial numbers were captured
            </Text>
          )}
        </Row>
        <Row label="Model">
          {job.collectedProduct ? (
            <>
              <span className="num">{job.collectedProduct.sku}</span>
              <Text fz="sm" c="gray.6">
                {job.collectedProduct.name}
              </Text>
            </>
          ) : (
            <Text component="span" fz="sm" c="gray.6">
              Not recorded — collected before the model was captured
            </Text>
          )}
        </Row>
        <Row label="Kind">
          {job.category ? (
            hierarchyPath(job)
          ) : (
            <Text component="span" fz="sm" c="gray.6">
              Not recorded at collection
            </Text>
          )}
        </Row>
        {/* Only when there IS one. The description is no longer asked for at
            collection - the model says what the battery is - so on anything
            collected since it is empty, and an empty "Description: none" row
            is a row that says nothing. Older jobs have real text here and it
            is the only description they have. */}
        {job.batteryDetails && <Row label="Description">{job.batteryDetails}</Row>}
        <Row label="Items">
          <span className="num">{job.itemCount}</span>
        </Row>
        {/* Only where a judgement was actually recorded. Warranty is not
            asked for any more - the Service Manager checks it manually - so
            on anything diagnosed since this is UNKNOWN, and a "Warranty: Not
            established" row would be a row that says nothing. */}
        {job.warrantyStatus !== "UNKNOWN" && (
          <Row label="Warranty (at the time)">
            {WARRANTY_LABELS[job.warrantyStatus] ?? job.warrantyStatus}
          </Row>
        )}
      </Panel>
    </SectionCard>
  );

  const customer = (
    <SectionCard title="Customer">
      <Panel>
        <Row label="Name">{job.customer.name}</Row>
        {job.customer.phone && (
          <Row label="Phone">
            <span className="num">{job.customer.phone}</span>
          </Row>
        )}
      </Panel>
    </SectionCard>
  );

  const proofs = job.proofs.length > 0 && (
    <SectionCard title="Proof captured">
      <Stack gap="xs">
        {job.proofs.map((proof) => (
          <Group key={proof.id} gap="sm" wrap="nowrap">
            <IconPhoto size={16} stroke={1.6} color="var(--mantine-color-gray-6)" />
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text fz="sm" c="gray.9" tt="capitalize">
                {proof.kind.toLowerCase().replace(/_/g, " ")}
              </Text>
              <Text fz="xs" c="gray.6">
                {when(proof.capturedAt)}
                {proof.capturedBy ? ` · ${proof.capturedBy.name}` : ""}
              </Text>
            </Stack>
          </Group>
        ))}
      </Stack>
    </SectionCard>
  );

  const order = job.order && (
    <SectionCard title="Order">
      <Stack gap="sm">
        <Panel>
          <Row label="Reference">
            <span className="num">{job.order.reference}</span>
          </Row>
          <Row label="Type">
            {job.order.type.replace(/_/g, " ").toLowerCase()}
          </Row>
          <Row label="Billed">
            {job.order.bill
              ? `${job.order.bill.reference} — ${formatINR(job.order.bill.totalAmount)}`
              : "Not yet"}
          </Row>
        </Panel>
        <Button
          variant="default"
          onClick={() => navigate(`/sales/bill/${job.order!.id}`)}
        >
          {job.order.bill ? "View the bill" : "Go to billing"}
        </Button>
      </Stack>
    </SectionCard>
  );

  const main = (
    <Stack gap="md">
      <SectionCard title="Where it has got to">
        <Stack gap="md">
          <Group gap="sm" wrap="wrap">
            <StateBadge state={serviceJobState(job.status) ?? "svcCollected"} />
            <Text fz="md" c="gray.7">
              {STATUS_LABELS[job.status] ?? job.status}
            </Text>
          </Group>
          <StageStepper stages={stages} currentIndex={current} />
        </Stack>
      </SectionCard>

      {job.diagnosedAt && job.diagnosisNotes && (
        <SectionCard title="Findings">
          <Stack gap={4}>
            <Text fz="md" c="gray.9">
              {job.diagnosisNotes}
            </Text>
            <Text fz="sm" c="gray.6">
              {job.technician ? `${job.technician.name} · ` : ""}
              {when(job.diagnosedAt)}
            </Text>
          </Stack>
        </SectionCard>
      )}

      {/* --- step 2 --- */}
      {job.status === "COLLECTED" && (
        <SectionCard title="Confirm it arrived">
          {canReceive ? (
            <Stack gap="sm">
              <Text fz="md" c="gray.7">
                Booking it in puts it in front of a technician. No photo is
                needed — this is a confirmation, not a field capture.
              </Text>
              <Button
                size="md"
                loading={busy}
                onClick={() =>
                  void act(async () => {
                    await api.receiveServiceJob(job.id);
                    notifications.show({
                      color: "teal",
                      message: `${job.reference} is at the workshop.`,
                    });
                    await load();
                  }, "Could not book it in.")
                }
              >
                Received at the workshop
              </Button>
            </Stack>
          ) : (
            <Text fz="md" c="gray.6">
              The workshop confirms arrival. You collected this one — it will
              appear here once someone books it in.
            </Text>
          )}
        </SectionCard>
      )}

      {/* --- step 3 --- */}
      {job.status === "RECEIVED" && (
        <SectionCard title="Diagnosis">
          {canDiagnose ? (
            <Stack gap="md">
              {/* No warranty field and no repairable checkbox. The business
                  judges warranty manually and told us it does not belong in
                  the app, and with the outcome stated outright a separate
                  repairable flag could only contradict it. */}
              <Radio.Group
                name="outcome"
                value={outcome}
                onChange={setOutcome}
                label="Outcome"
                withAsterisk
              >
                <Stack gap="sm" mt="xs">
                  {OUTCOMES.map((option) => (
                    <Radio
                      key={option.value}
                      id={`outcome-${option.value}`}
                      value={option.value}
                      label={option.label}
                      // The consequence is not decoration: these four differ
                      // in whether stock moves and whether anything is
                      // billed, and that is invisible from the name.
                      description={option.consequence}
                    />
                  ))}
                </Stack>
              </Radio.Group>

              <TextInput
                id="findings"
                label="What you found"
                withAsterisk
                maxLength={2000}
                placeholder="e.g. cell failure, will not hold charge"
                value={diagnosisNotes}
                onChange={(event) => setDiagnosisNotes(event.currentTarget.value)}
                description="This is the record of the diagnosis."
              />

              <Button
                size="md"
                disabled={!diagnosisReady}
                loading={busy}
                onClick={() =>
                  void act(async () => {
                    await api.diagnoseServiceJob(job.id, {
                      outcome,
                      notes: diagnosisNotes.trim(),
                    });
                    notifications.show({ color: "teal", message: "Diagnosis recorded." });
                    await load();
                  }, "Could not record the diagnosis.")
                }
              >
                Record the diagnosis
              </Button>
            </Stack>
          ) : (
            <Text fz="md" c="gray.6">
              A technician or the Service Manager diagnoses this one.
            </Text>
          )}
        </SectionCard>
      )}

      {/* --- step 4 --- */}
      {job.status === "DIAGNOSED" && !job.order && (
        <SectionCard title={raisesOrder ? "Place the replacement order" : "Carry it out"}>
          {canComplete ? (
            raisesOrder ? (
              <Stack gap="md">
                <Alert
                  variant="light"
                  color="orange"
                  radius="md"
                  icon={<IconAlertTriangle size={20} stroke={1.8} />}
                  title="A new battery goes out from stock"
                >
                  {isProrata
                    ? "The customer pays the amount you enter below, and stock is deducted when the Billing Executive bills it."
                    : "The bill is zero, but stock is deducted when the Billing Executive bills it."}
                </Alert>
                <Text fz="md" c="gray.7">
                  Placed the same way as any order — pick the battery from the
                  catalogue below. It then appears in the Billing
                  Executive&apos;s queue.
                </Text>

                <Text fz="sm" fw={600} c="gray.9">
                  Battery going out
                </Text>
                <ProductPicker lines={lines} onChange={setLines} />

                {lines.length > 0 && lines.length !== job.itemCount && (
                  <Text fz="sm" c="gray.6">
                    {job.itemCount} battery(ies) came in and{" "}
                    {lines.reduce((sum, line) => sum + line.quantity, 0)} are
                    going out. That is allowed — just worth a second look.
                  </Text>
                )}

                {isProrata ? (
                  <Stack gap="xs">
                    <MoneyField
                      id="prorata-amount"
                      label="What the customer pays"
                      withAsterisk
                      min={0.01}
                      placeholder="1200.00"
                      value={prorataAmount}
                      onChange={setProrataAmount}
                      description="A flat amount, before GST — your judgement of how much of the battery they pay for. Nobody changes it later: the Billing Executive bills this figure as it stands."
                      error={
                        prorataOverValue
                          ? `${formatINR(prorataValue)} is more than the ${formatINR(
                              linesGross,
                            )} the batteries are worth. Pro rata means they pay part of it — if they are paying full price, that is a regular sale.`
                          : String(prorataAmount).trim() !== "" && !prorataValid
                            ? "Enter an amount in rupees and paise, more than zero."
                            : undefined
                      }
                    />
                    {prorataValid && !prorataOverValue && (
                      <Alert variant="light" color="teal" radius="md" p="sm">
                        <Text fz="sm" className="num">
                          {formatINR(prorataValue)} + {gstRate}% GST ={" "}
                          {formatINR(prorataWithGst)} on the invoice.
                          {linesGross > 0 &&
                            ` The batteries are worth ${formatINR(linesGross)}.`}
                        </Text>
                      </Alert>
                    )}
                  </Stack>
                ) : (
                  <Text fz="sm" c="gray.6">
                    The customer pays nothing. The order bills zero, and stock
                    is deducted when the Billing Executive bills it.
                  </Text>
                )}

                {/* WHO TAKES IT OUT - the Service Manager's call, on a
                    replacement as much as on a return.

                    The wording differs from the OK/Rejected case below
                    because the mechanics differ, and the difference is worth
                    saying: the delivery task for a replacement is not created
                    here, it is created when the Billing Executive bills it.
                    So this is a name that waits, and the Billing Executive
                    can change it when they get there. */}
                <DeliveryAgentPicker
                  value={deliveryAgentId}
                  onChange={setDeliveryAgentId}
                  label="Delivery agent for the new battery"
                  hint={
                    job.collectionAgent
                      ? `${job.collectionAgent.name} collected the old battery, so they have already been to this address.`
                      : undefined
                  }
                  blankMeans="Nobody is named, so the delivery will wait in the Store Manager's list once the bill is raised."
                />
                <Text fz="sm" c="gray.6">
                  Recorded against the order now and acted on when the Billing
                  Executive bills it — they can change it if somebody else is
                  going.
                </Text>
              </Stack>
            ) : (
              <Stack gap="md">
                <Text fz="md" c="gray.7">
                  {job.outcome === "OK"
                    ? "The battery is OK and goes back to the customer. Nothing is sold, so there is no order and nothing to bill — it goes straight to Delivery."
                    : "Nothing can be done for this battery, so it goes back to its owner. Nothing is sold, so there is no order and nothing to bill — it goes straight to Delivery."}
                </Text>
                {/* Assigned outright here: there is no bill on this path, so
                    this step IS the handover to Delivery. */}
                <DeliveryAgentPicker
                  value={deliveryAgentId}
                  onChange={setDeliveryAgentId}
                  label="Delivery agent taking it back"
                  hint={
                    job.collectionAgent
                      ? `${job.collectionAgent.name} collected it, so they have already been to this address and carried this battery once.`
                      : undefined
                  }
                />
              </Stack>
            )
          ) : (
            <Text fz="md" c="gray.6">
              The Service Manager carries the outcome out — it is what commits
              it and, for a replacement, decides whether stock moves.
            </Text>
          )}
        </SectionCard>
      )}
    </Stack>
  );

  const rail = (
    <Stack gap="md">
      {customer}
      {battery}
      {order}
      {proofs}
    </Stack>
  );

  /**
   * The step's primary action, in the sticky bar.
   *
   * Placing the order goes through a confirmation, because it is the point at
   * which a battery is committed to leave the building - free, for an FOC -
   * and the brief's rule is that an irreversible action states its
   * consequence first. Receiving and diagnosing do not: both move the job
   * forward and neither gives anything away.
   */
  const placeOrder = () => {
    const going = lines.reduce((sum, line) => sum + line.quantity, 0);
    modals.openConfirmModal({
      title: raisesOrder && isProrata ? "Place the pro-rata order" : "Place the free order",
      children: (
        <Text fz="md">
          {going} batter{going === 1 ? "y" : "ies"} will be committed to leave
          stock, and{" "}
          {isProrata
            ? `the customer will be billed ${formatINR(prorataValue)} plus ${gstRate}% GST.`
            : "the customer will be billed nothing."}{" "}
          Stock is deducted when the Billing Executive generates the bill. The
          amount cannot be changed after this.
        </Text>
      ),
      labels: { confirm: "Place the order", cancel: "Not yet" },
      onConfirm: () =>
        void act(async () => {
          const result = await api.completeServiceJob(job.id, {
            lineItems: lines.map((line) => ({
              productId: line.productId,
              quantity: line.quantity,
            })),
            // Sent only for a pro-rata replacement. The API refuses an amount
            // on any other outcome, which is what stops an FOC quietly
            // carrying a charge.
            ...(isProrata ? { prorataAmount: prorataValue } : {}),
            // Omitted when blank, which the API reads as "nobody named".
            // Sent as an empty string it would be a validation failure on a
            // field the manager deliberately left alone.
            ...(deliveryAgentId ? { deliveryAgentId } : {}),
          });
          setFinished({
            outcome: job.outcome,
            ...(result.order
              ? {
                  reference: result.order.reference,
                  type: result.order.type,
                  orderId: result.order.id,
                }
              : {}),
          });
        }, "Could not place the order."),
    });
  };

  const action =
    job.status === "DIAGNOSED" && !job.order && canComplete ? (
      raisesOrder ? (
        <StickyActionBar
          context={
            isProrata && prorataValid && !prorataOverValue
              ? `Customer pays ${formatINR(prorataWithGst)} incl. GST`
              : undefined
          }
          primary={
            <Button
              size="md"
              fullWidth
              disabled={
                busy ||
                lines.length === 0 ||
                (isProrata && (!prorataValid || prorataOverValue))
              }
              onClick={placeOrder}
            >
              Place the order
            </Button>
          }
        />
      ) : (
        <StickyActionBar
          primary={
            <Button
              size="md"
              fullWidth
              loading={busy}
              onClick={() =>
                void act(async () => {
                  await api.completeServiceJob(job.id, {
                    ...(deliveryAgentId ? { deliveryAgentId } : {}),
                  });
                  setFinished({ outcome: job.outcome });
                }, "Could not complete the job.")
              }
            >
              {job.outcome === "OK"
                ? "OK — ready to go back"
                : "Ready to go back to the customer"}
            </Button>
          }
        />
      )
    ) : undefined;

  return (
    <Page
      title={job.reference}
      subtitle={job.customer.name}
      back="/service/queue"
      width="wide"
    >
      {error && <ErrorState message={error} />}
      <RecordWithRail main={main} rail={rail} />
      {action}
    </Page>
  );
}
